import {
    autoUpdate,
    flip,
    offset,
    shift,
    useFloating,
} from "@floating-ui/react";
import { faPlus, faStar, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { faStar as faStarSolid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faExclamationTriangle,
    faTriangleExclamation,
} from "@fortawesome/sharp-light-svg-icons";
import { Combobox } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { useClickAway } from "@uidotdev/usehooks";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
    useController,
    useFieldArray,
    useFormContext,
    useWatch,
} from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import type { EmissionRecordView } from "../../../apiClient/generated";
import { usePermission } from "../../../auth";
import {
    useAccountsApiClient,
    useEmissionEventsApiClient,
    useEmissionRecordsApiClient,
    useInfrastructureApiClient,
} from "../../../hooks";
import { formatDateTime } from "../../../utils/dates";
import {
    EquipmentTree,
    type EquipmentWithChildren,
} from "../../Infrastructure/EquipmentTree";
import { evaluateSuggestion, evaluateVisibility } from "../conditionEvaluator";
import { EventFiles } from "../EventFiles";
import type { FieldDefinition, TableColumn } from "../formDefinition";
import {
    type EventFormFieldProps,
    FormFieldBase,
    SuggestionBox,
    shouldShowSuggestion,
} from "./base";
import { FormDateInput, FormDateTimeInput } from "./DateTimeField";

export const FormTextInput = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const fieldName = props.fieldDefinition.fieldName;

    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    return (
        <FormFieldBase
            label={props.fieldDefinition.label}
            error={fieldState.error}
        >
            <input
                className={`rounded px-2 py-1.5 text-sm w-full ${fieldState.error ? "bg-red-50 border-red-500 text-red-500" : "border-neutral-200"}`}
                placeholder={props.fieldDefinition.placeholder}
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
            />
            {shouldShowSuggestion(props, field.value) && (
                <SuggestionBox
                    suggestedValueLabel={String(props.suggestedValue)}
                    onApply={() => field.onChange(props.suggestedValue)}
                />
            )}
        </FormFieldBase>
    );
};

export const FormTextArea = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const fieldName = props.fieldDefinition.fieldName;

    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    return (
        <FormFieldBase
            label={props.fieldDefinition.label}
            error={fieldState.error}
        >
            <textarea
                className={`rounded px-2 py-1.5 text-sm w-full min-h-10 ${fieldState.error ? "bg-red-50 border-red-500 text-red-500" : "border-neutral-200"}`}
                placeholder={props.fieldDefinition.placeholder}
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
            />
            {shouldShowSuggestion(props, field.value) && (
                <SuggestionBox
                    suggestedValueLabel={String(props.suggestedValue)}
                    onApply={() => field.onChange(props.suggestedValue)}
                />
            )}
        </FormFieldBase>
    );
};

export const FormCheckbox = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const fieldName = props.fieldDefinition.fieldName;

    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    return (
        <FormFieldBase error={fieldState.error}>
            <label className="flex items-center gap-2">
                <input
                    type="checkbox"
                    className="text-ae-blue-500 rounded"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    onBlur={field.onBlur}
                    ref={field.ref}
                />
                <span className="text-neutral-500">
                    {props.fieldDefinition.label}
                </span>
            </label>
            {shouldShowSuggestion(props, field.value) && (
                <SuggestionBox
                    suggestedValueLabel={props.suggestedValue ? "Yes" : "No"}
                    onApply={() =>
                        field.onChange(Boolean(props.suggestedValue))
                    }
                />
            )}
        </FormFieldBase>
    );
};

const formatStatus = (s: string | undefined) =>
    s
        ? s
              .replaceAll("_", " ")
              .toLowerCase()
              .replace(/^\w/, (c) => c.toUpperCase())
        : "-";

const EmissionIdSearch = ({
    onSubmit,
    onCancel,
    error,
}: {
    onSubmit: (id: string) => void;
    onCancel: () => void;
    error?: boolean;
}) => {
    const [showSearch, setShowSearch] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const opened = showSearch || error;

    if (!opened) {
        return (
            <button
                type="button"
                className="text-xs text-ae-blue-550 self-start text-left"
                onClick={() => setShowSearch(true)}
            >
                Search for an event to link by emission ID
            </button>
        );
    }

    return (
        <>
            {error && (
                <p className="text-xs text-red-500 mb-1">
                    Could not find emission with that ID.
                </p>
            )}
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    className="flex-1 rounded border border-neutral-200 px-2 py-1 text-xs"
                    placeholder="Enter emission ID"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />
                <button
                    type="button"
                    className="rounded bg-ae-blue-550 px-2 py-1 text-xs text-white hover:bg-ae-blue-600 disabled:opacity-50"
                    disabled={!searchInput.trim()}
                    onClick={() => {
                        onSubmit(searchInput.trim());
                        setShowSearch(false);
                        setSearchInput("");
                    }}
                >
                    Search
                </button>
                <button
                    type="button"
                    className="text-xs text-neutral-500 hover:text-neutral-700"
                    onClick={() => {
                        setShowSearch(false);
                        setSearchInput("");
                        onCancel();
                    }}
                >
                    Cancel
                </button>
            </div>
        </>
    );
};

export const FormEventLinking = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const emissionsApiClient = useEmissionRecordsApiClient();
    const eventsApiClient = useEmissionEventsApiClient();
    const fieldName = props.fieldDefinition.fieldName;

    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    const isNewState =
        props.emissionRecord.mainEvent.eventStatus === "NEW" ||
        props.emissionRecord.mainEvent.eventStatus === "CREATED";
    const isWip =
        props.emissionRecord.mainEvent.eventStatus === "WORK_IN_PROGRESS";
    const allowLinking = isNewState || isWip;

    const similarEmissionsQuery = useQuery({
        queryKey: ["similarEmissions", props.emissionRecord.id],
        queryFn: () =>
            emissionsApiClient.emissionRecordsGetNearbyEventsList({
                id: props.emissionRecord.id,
            }),
        refetchOnWindowFocus: false,
        enabled: isNewState,
    });

    const [emissionToLink, setEmissionToLink] = useState<string | undefined>();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [linkMode, setLinkMode] = useState<"link" | "skip">(() =>
        field.value ? "link" : "skip",
    );

    const emissionToLinkQuery = useQuery({
        queryKey: ["emissionToLink", emissionToLink],
        queryFn: () =>
            emissionsApiClient.emissionRecordsRetrieve({ id: emissionToLink! }),
        refetchOnWindowFocus: false,
        retry: false,
        enabled: !!emissionToLink,
    });

    // If the searched record is itself linked, follow the chain to the canonical record.
    useEffect(() => {
        const linkedEventId = emissionToLinkQuery.data?.mainEvent.mainEvent;
        if (!linkedEventId) return;
        let cancelled = false;
        eventsApiClient
            .emissioneventsRetrieve({ id: linkedEventId })
            .then((mainEvent) => {
                if (cancelled) return;
                if (mainEvent.mainEmissionRecord) {
                    setEmissionToLink(mainEvent.mainEmissionRecord);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [emissionToLinkQuery.data, eventsApiClient]);

    const items = useMemo(
        () => [
            ...(emissionToLinkQuery.data ? [emissionToLinkQuery.data] : []),
            ...(similarEmissionsQuery.data ?? []),
        ],
        [emissionToLinkQuery.data, similarEmissionsQuery.data],
    );

    const slotCount = items.length;
    const safeIndex = slotCount > 0 ? Math.min(currentIndex, slotCount - 1) : 0;
    const r = slotCount > 0 ? items[safeIndex] : undefined;
    const isSelf = !!r && r.id === props.emissionRecord.id;
    const isLinked = !!r && r.mainEvent?.eventStatus === "LINKED";
    const isDisabled = isSelf || isLinked;

    const writeFieldValue = (mode: "link" | "skip", index: number) => {
        const target = items[index];
        const targetDisabled =
            !target ||
            target.id === props.emissionRecord.id ||
            target.mainEvent?.eventStatus === "LINKED";
        const next =
            mode === "skip" || targetDisabled
                ? null
                : (target.mainEvent?.id ?? null);
        if (field.value !== next) field.onChange(next);
    };

    if (!allowLinking) return null;

    if (isWip && !emissionToLinkQuery.data) {
        return (
            <EmissionIdSearch
                error={emissionToLinkQuery.isError}
                onSubmit={(id) => {
                    setEmissionToLink(id);
                    setCurrentIndex(0);
                }}
                onCancel={() => setEmissionToLink(undefined)}
            />
        );
    }

    return (
        <FormFieldBase
            label={
                isWip ? "Link to existing event" : props.fieldDefinition.label
            }
            error={fieldState.error}
        >
            {r && (
                <div
                    className={`border border-neutral-200 rounded p-2 ${linkMode === "skip" ? "opacity-60" : ""}`}
                >
                    {slotCount > 1 && (
                        <div className="flex items-center justify-between mb-2 text-xs">
                            <button
                                type="button"
                                disabled={safeIndex === 0}
                                onClick={() => {
                                    const next = safeIndex - 1;
                                    setCurrentIndex(next);
                                    writeFieldValue(linkMode, next);
                                }}
                                className={`px-2 py-1 border rounded ${safeIndex === 0 ? "opacity-40 cursor-not-allowed" : "hover:bg-neutral-100"}`}
                            >
                                <FontAwesomeIcon
                                    icon={faChevronLeft}
                                    className="h-3 w-3"
                                />
                            </button>
                            <span className="text-neutral-600">
                                {`${safeIndex + 1} / ${slotCount}`}
                            </span>
                            <button
                                type="button"
                                disabled={safeIndex === slotCount - 1}
                                onClick={() => {
                                    const next = safeIndex + 1;
                                    setCurrentIndex(next);
                                    writeFieldValue(linkMode, next);
                                }}
                                className={`px-2 py-1 border rounded ${safeIndex === slotCount - 1 ? "opacity-40 cursor-not-allowed" : "hover:bg-neutral-100"}`}
                            >
                                <FontAwesomeIcon
                                    icon={faChevronRight}
                                    className="h-3 w-3"
                                />
                            </button>
                        </div>
                    )}

                    {isDisabled && (
                        <div className="flex items-start gap-1.5 rounded bg-yellow-50 text-yellow-800 text-xs px-2 py-1.5 mb-2">
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                className="h-3 w-3 mt-0.5 shrink-0"
                            />
                            <span>
                                {isSelf
                                    ? "Emissions cannot be linked to themselves."
                                    : "This emission is already linked to another event."}
                            </span>
                        </div>
                    )}

                    <div className="text-xs space-y-0.5">
                        <div className="flex justify-between gap-2">
                            <span className="text-neutral-500">Provider:</span>
                            <span className="font-medium text-right">
                                {r.providerName}
                            </span>
                        </div>
                        {r.dataPoint?.secondaryDataSource && (
                            <div className="flex justify-between gap-2">
                                <span className="text-neutral-500">
                                    Secondary source:
                                </span>
                                <span className="text-right">
                                    {r.dataPoint.secondaryDataSource}
                                </span>
                            </div>
                        )}
                        <div className="flex justify-between gap-2">
                            <span className="text-neutral-500">Detected:</span>
                            <span className="text-right">
                                {formatDateTime(r.dataPoint.detectionTimestamp)}
                            </span>
                        </div>
                        <div className="flex justify-between gap-2">
                            <span className="text-neutral-500">Rate:</span>
                            <span className="text-right">
                                {r.dataPoint.detectedRate
                                    ? `${(r.dataPoint.detectedRate / 1000).toFixed(2)} kg/h`
                                    : r.dataPoint.concentration != null
                                      ? `${r.dataPoint.concentration} ppm*m`
                                      : "-"}
                            </span>
                        </div>
                        <div className="flex justify-between gap-2">
                            <span className="text-neutral-500">Status:</span>
                            <span className="text-right">
                                {formatStatus(r.mainEvent?.eventStatus)}
                            </span>
                        </div>
                        {r.mainEvent?.formData.ae_root_cause && (
                            <div className="flex justify-between gap-2">
                                <span className="text-neutral-500">
                                    Root cause:
                                </span>
                                <span className="text-right">
                                    {r.mainEvent?.formData.ae_root_cause}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="mt-2 flex flex-col gap-1 text-xs">
                {slotCount > 0 && (
                    <label className="flex items-center gap-2">
                        <input
                            type="radio"
                            checked={linkMode === "link"}
                            disabled={isDisabled}
                            onChange={() => {
                                setLinkMode("link");
                                writeFieldValue("link", safeIndex);
                            }}
                        />
                        <span>Link to selected emission</span>
                    </label>
                )}
                <label className="flex items-center gap-2">
                    <input
                        type="radio"
                        checked={linkMode === "skip"}
                        onChange={() => {
                            setLinkMode("skip");
                            writeFieldValue("skip", safeIndex);
                        }}
                    />
                    <span>Don't link — create a new event</span>
                </label>
            </div>

            <div className="mt-2">
                <EmissionIdSearch
                    error={emissionToLinkQuery.isError}
                    onSubmit={(id) => {
                        setEmissionToLink(id);
                        setCurrentIndex(0);
                    }}
                    onCancel={() => setEmissionToLink(undefined)}
                />
            </div>
        </FormFieldBase>
    );
};

export const FormSite = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const fieldName = props.fieldDefinition.fieldName;

    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    const options = useMemo(() => {
        if (!props.emissionRecord.siteMatches) {
            return [];
        }
        return props.emissionRecord.siteMatches
            .map((i) => {
                return {
                    id: i.id,
                    displayName: `${i.siteName} (${
                        i.location &&
                        i.location.coordinates
                            .toReversed()
                            .map((c) => c.toFixed(4))
                            .join(", ")
                    })`,
                };
            })
            .toSorted((a, b) =>
                a.displayName.localeCompare(b.displayName, undefined, {
                    numeric: true,
                }),
            );
    }, [props.emissionRecord.siteMatches]);

    return (
        <FormFieldBase
            label={props.fieldDefinition.label}
            error={fieldState.error}
        >
            <select
                value={field.value ?? ""}
                onChange={(e) => {
                    if (e.target.value !== "") {
                        field.onChange(e.target.value);
                    } else {
                        field.onChange(null);
                    }
                    field.onBlur();
                }}
                className={`rounded px-2 py-1.5 text-sm w-full ${fieldState.error ? "bg-red-50 border-red-500 text-red-500" : "border-neutral-200"}`}
            >
                {options.map((site) => (
                    <option key={site.id} value={site.id}>
                        {site.displayName}
                    </option>
                ))}
                <option value="">
                    Event not associated with my infrastructure.
                </option>
            </select>
        </FormFieldBase>
    );
};

export const FormEquipment = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const apiClient = useInfrastructureApiClient();
    const fieldName = props.fieldDefinition.fieldName;
    const selectedSite = form.watch("site");

    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    const [open, setOpen] = useState(false);

    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [offset({ mainAxis: 4 }), shift(), flip()],
        whileElementsMounted: autoUpdate,
    });

    const clickAwayRef = useClickAway<HTMLDivElement>(() => setOpen(false));

    const selectedEquipmentQuery = useQuery({
        queryKey: ["equipment", field.value],
        queryFn: () =>
            apiClient.infrastructureEquipmentRetrieve({ id: field.value }),
        enabled: !!field.value,
        refetchOnWindowFocus: false,
    });

    const handleEquipmentSelect = (equipment: EquipmentWithChildren) => {
        field.onChange(equipment.id);
        field.onBlur();
        setOpen(false);
    };

    return (
        <FormFieldBase
            label={props.fieldDefinition.label}
            error={fieldState.error}
        >
            {field.value && selectedEquipmentQuery.data ? (
                <div className="flex items-center justify-between rounded border border-neutral-200 px-2 py-1.5 text-sm bg-ae-gray-50">
                    <div>
                        <span className="font-medium">
                            {selectedEquipmentQuery.data.equipmentClass ||
                                selectedEquipmentQuery.data.groupName}
                        </span>
                        {selectedEquipmentQuery.data.operatorUniqueId && (
                            <span className="text-ae-gray-500 ml-1">
                                ({selectedEquipmentQuery.data.operatorUniqueId})
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            field.onChange(null);
                            field.onBlur();
                        }}
                    >
                        <XMarkIcon className="w-4 h-4 text-ae-gray-400 hover:text-ae-gray-600" />
                    </button>
                </div>
            ) : (
                <button
                    ref={refs.setReference}
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="text-sm rounded border border-neutral-200 px-2 py-1.5 w-full text-left text-ae-gray-400 hover:border-ae-blue-400"
                >
                    Select equipment…
                </button>
            )}

            {open && (
                <div
                    ref={(el) => {
                        refs.setFloating(el);
                        (
                            clickAwayRef as React.MutableRefObject<HTMLDivElement | null>
                        ).current = el;
                    }}
                    style={{ ...floatingStyles, minWidth: 480, maxHeight: 360 }}
                    className="z-50 bg-white rounded border border-ae-gray-250 drop-shadow-lg overflow-hidden"
                >
                    <EquipmentTree
                        siteId={selectedSite}
                        onClickRow={handleEquipmentSelect}
                    />
                </div>
            )}
        </FormFieldBase>
    );
};

export const FormUser = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const apiClient = useAccountsApiClient();
    const fieldName = props.fieldDefinition.fieldName;
    const [query, setQuery] = useState("");

    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    const approverUsersOnly =
        props.fieldDefinition.metadata?.approverUsersOnly ?? false;
    const canApprove = usePermission("emissionEventApprove");

    const peopleQuery = useQuery({
        queryKey: [
            "companyUserList",
            props.emissionRecord.owner,
            approverUsersOnly,
        ],
        queryFn: async () => {
            return await apiClient.accountsUsersList({
                companymembershipCompany: props.emissionRecord.owner,
                hasApprovalPermission: approverUsersOnly ? true : undefined,
            });
        },
        placeholderData: [],
        // Skip the fetch entirely when this field requires approval permission
        // and the current user doesn't have it — they won't see the field anyway.
        enabled: !approverUsersOnly || canApprove,
    });

    // Hide coordinator field for users who lack the approval permission.
    if (approverUsersOnly && !canApprove) {
        return null;
    }

    const filteredUsers =
        query === ""
            ? peopleQuery.data
            : peopleQuery.data?.filter((person) =>
                  `${person.firstName.toLowerCase()} ${person.lastName.toLowerCase()} (${person.email})`.includes(
                      query.toLowerCase(),
                  ),
              );

    if (!peopleQuery.data) return null;

    return (
        <FormFieldBase
            label={props.fieldDefinition.label}
            error={fieldState.error}
        >
            <Combobox value={field.value} onChange={field.onChange}>
                <div className="relative">
                    <div
                        className={`flex rounded border ${
                            fieldState.error
                                ? "border-red-500"
                                : "border-neutral-300"
                        }`}
                    >
                        <Combobox.Input
                            onChange={(event) => setQuery(event.target.value)}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            displayValue={(id: number) => {
                                const selectedUser = peopleQuery.data?.find(
                                    (u) => u.id === id,
                                );
                                if (!selectedUser) {
                                    return "";
                                }
                                return `${selectedUser.firstName} ${selectedUser.lastName} (${selectedUser.email})`;
                            }}
                            className={`px-2 py-1.5 text-sm w-full border-none outline-none rounded ${
                                fieldState.error
                                    ? "bg-red-50 text-red-500"
                                    : "bg-white text-neutral-800"
                            }`}
                        />
                        <Combobox.Button className="absolute h-full right-0 px-3 text-neutral-500 hover:text-neutral-700 transition-colors">
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="w-3 h-3"
                            />
                        </Combobox.Button>
                    </div>

                    <Combobox.Options className="absolute z-10 mt-1 w-full bg-white border border-neutral-200 rounded shadow-lg max-h-60 overflow-auto focus:outline-none">
                        {filteredUsers?.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-neutral-400">
                                No results found.
                            </div>
                        ) : (
                            filteredUsers?.map((user) => (
                                <Combobox.Option
                                    key={user.id}
                                    value={user.id}
                                    className={({ active, selected }) =>
                                        `px-2 py-1 text-sm cursor-pointer ${
                                            selected
                                                ? "bg-blue-50 text-blue-700 font-medium"
                                                : active
                                                  ? "bg-neutral-50 text-neutral-900"
                                                  : "text-neutral-700"
                                        }`
                                    }
                                >
                                    {user.firstName} {user.lastName}{" "}
                                    <span className="italic">
                                        ({user.email})
                                    </span>
                                </Combobox.Option>
                            ))
                        )}
                    </Combobox.Options>
                </div>
            </Combobox>
        </FormFieldBase>
    );
};

export const FormNumber = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const fieldName = props.fieldDefinition.fieldName;

    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    return (
        <FormFieldBase
            label={props.fieldDefinition.label}
            error={fieldState.error}
        >
            <input
                type="number"
                className={`rounded px-2 py-1.5 text-sm w-full [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${fieldState.error ? "bg-red-50 border-red-500 text-red-500" : "border-neutral-200"}`}
                placeholder={props.fieldDefinition.placeholder}
                value={field.value ?? ""}
                onWheel={(e) => e.currentTarget.blur()}
                onChange={(e) =>
                    field.onChange(
                        e.target.value === "" ? null : Number(e.target.value),
                    )
                }
                onBlur={field.onBlur}
                ref={field.ref}
            />
            {shouldShowSuggestion(props, field.value) && (
                <SuggestionBox
                    suggestedValueLabel={String(props.suggestedValue)}
                    onApply={() => field.onChange(Number(props.suggestedValue))}
                />
            )}
        </FormFieldBase>
    );
};

const OTHER_PREFIX = "Other: ";
export const FormSelect = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const fieldName = props.fieldDefinition.fieldName;
    const choices = props.fieldDefinition.metadata?.choices ?? [];
    const allowAny = props.fieldDefinition.metadata?.allowAny ?? false;
    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    const currentValue: string = field.value ?? "";
    const isOther = allowAny && currentValue.startsWith(OTHER_PREFIX);
    const otherText = isOther ? currentValue.slice(OTHER_PREFIX.length) : "";

    const errorClasses = fieldState.error
        ? "bg-red-50 border-red-500 text-red-500"
        : "border-neutral-200";

    return (
        <FormFieldBase
            label={props.fieldDefinition.label}
            error={fieldState.error}
        >
            <div className="flex items-center gap-1">
                <select
                    value={isOther ? "__other__" : currentValue}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val === "__other__") {
                            field.onChange(OTHER_PREFIX);
                            return;
                        }
                        field.onChange(val !== "" ? val : null);
                        field.onBlur();
                    }}
                    className={`rounded px-2 py-1.5 text-sm w-full border ${errorClasses}`}
                >
                    <option value="">— Select —</option>
                    {choices.map((choice) => (
                        <option key={choice} value={choice}>
                            {choice}
                        </option>
                    ))}
                    {allowAny && <option value="__other__">Other</option>}
                </select>

                {isOther && (
                    <input
                        type="text"
                        autoFocus
                        value={otherText}
                        onChange={(e) =>
                            field.onChange(OTHER_PREFIX + e.target.value)
                        }
                        onBlur={field.onBlur}
                        placeholder="Please specify"
                        className={`rounded px-2 py-1.5 text-sm w-full border ${errorClasses}`}
                    />
                )}
            </div>
            {shouldShowSuggestion(props, field.value) && (
                <SuggestionBox
                    suggestedValueLabel={props.suggestedValue as string}
                    onApply={() => field.onChange(props.suggestedValue)}
                />
            )}
        </FormFieldBase>
    );
};

export const FormYesNoRadio = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const fieldName = props.fieldDefinition.fieldName;

    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    return (
        <FormFieldBase
            label={props.fieldDefinition.label}
            error={fieldState.error}
        >
            <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 text-sm text-neutral-700">
                    <input
                        type="radio"
                        className="text-ae-blue-500"
                        checked={field.value === true}
                        onChange={() => {
                            field.onChange(true);
                            field.onBlur();
                        }}
                    />
                    Yes
                </label>
                <label className="flex items-center gap-1.5 text-sm text-neutral-700">
                    <input
                        type="radio"
                        className="text-ae-blue-500"
                        checked={field.value === false}
                        onChange={() => {
                            field.onChange(false);
                            field.onBlur();
                        }}
                    />
                    No
                </label>
            </div>
            {shouldShowSuggestion(props, field.value) && (
                <SuggestionBox
                    suggestedValueLabel={props.suggestedValue ? "Yes" : "No"}
                    onApply={() =>
                        field.onChange(Boolean(props.suggestedValue))
                    }
                />
            )}
        </FormFieldBase>
    );
};

export const FormStarRating = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const fieldName = props.fieldDefinition.fieldName;

    const { field } = useController({
        name: fieldName,
        control: form.control,
    });

    return (
        <FormFieldBase label={props.fieldDefinition.label}>
            <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((score) => (
                    <button
                        key={score}
                        type="button"
                        onClick={() => {
                            field.onChange(score);
                            field.onBlur();
                        }}
                        className="text-ae-blue-500 hover:text-ae-blue-600 transition-colors"
                    >
                        <FontAwesomeIcon
                            icon={field.value >= score ? faStarSolid : faStar}
                            className="w-5 h-5"
                        />
                    </button>
                ))}
            </div>
        </FormFieldBase>
    );
};

const TableCellField = ({
    fieldName,
    column,
}: {
    fieldName: string;
    column: TableColumn;
}) => {
    const form = useFormContext();
    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    const inputClass = `rounded px-2 py-1 text-sm w-full border ${fieldState.error ? "bg-red-50 border-red-500 text-red-500" : "border-neutral-200"}`;

    let input: ReactNode;

    if (column.field_type === "boolean") {
        input = (
            <input
                type="checkbox"
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
            />
        );
    } else if (column.field_type === "choice") {
        input = (
            <select
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value || null)}
                className={inputClass}
            >
                <option value="">—</option>
                {(column.choices ?? []).map((c) => (
                    <option key={c} value={c}>
                        {c}
                    </option>
                ))}
            </select>
        );
    } else if (column.field_type === "date") {
        input = (
            <input
                type="date"
                value={field.value ?? ""}
                onChange={field.onChange}
                className={inputClass}
            />
        );
    } else if (column.field_type === "datetime") {
        input = (
            <input
                type="datetime-local"
                value={field.value ?? ""}
                onChange={field.onChange}
                className={inputClass}
            />
        );
    } else if (column.field_type === "number") {
        input = (
            <input
                type="number"
                value={field.value ?? ""}
                onChange={(e) =>
                    field.onChange(
                        e.target.value === "" ? null : Number(e.target.value),
                    )
                }
                className={inputClass}
            />
        );
    } else {
        input = (
            <input
                type="text"
                value={field.value ?? ""}
                onChange={field.onChange}
                className={inputClass}
            />
        );
    }

    return (
        <div>
            <p className="text-sm mb-0.5 text-neutral-500">{column.label}</p>
            {input}
            {fieldState.error && (
                <p className="text-red-500 text-sm mt-0.5">
                    {fieldState.error.message}
                </p>
            )}
        </div>
    );
};

export const TableField = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const fieldName = props.fieldDefinition.fieldName;
    const columns: TableColumn[] =
        props.fieldDefinition.metadata?.columns ?? [];

    const { fields, append, remove } = useFieldArray({
        name: fieldName,
        control: form.control,
    });

    const emptyRow = Object.fromEntries(
        columns.map((c) => [c.field_name, null]),
    );

    return (
        <FormFieldBase label={props.fieldDefinition.label}>
            <div className="flex flex-col gap-2">
                {fields.map((item, rowIndex) => (
                    <div
                        key={item.id}
                        className="border rounded border-neutral-200 overflow-hidden p-1 flex flex-col gap-2 hover:bg-neutral-50"
                    >
                        <div className="bg-neutral-200 -m-1 p-1 px-2 flex items-center justify-between">
                            <span className="text-neutral-700 font-bold">
                                Item {rowIndex + 1}
                            </span>
                            <button
                                type="button"
                                onClick={() => remove(rowIndex)}
                                className="text-sm text-red-500 hover:text-red-700"
                            >
                                <FontAwesomeIcon
                                    icon={faTrash}
                                    className="mr-1"
                                />
                                Remove
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                            {columns.map((col) => (
                                <TableCellField
                                    key={col.field_name}
                                    fieldName={`${fieldName}.${rowIndex}.${col.field_name}`}
                                    column={col}
                                />
                            ))}
                        </div>
                    </div>
                ))}
                <button
                    type="button"
                    onClick={() => append(emptyRow)}
                    className="text-sm text-ae-blue-500 hover:text-ae-blue-700 self-start"
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    Add row
                </button>
            </div>
        </FormFieldBase>
    );
};

export const FormWarning = (props: EventFormFieldProps) => {
    return (
        <div className="flex items-center gap-2 rounded px-2 py-1 text-xs bg-yellow-200 text-yellow-900">
            <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-yellow-700"
            />
            {props.fieldDefinition.label}
        </div>
    );
};

export const FormAuto = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const fieldName = props.fieldDefinition.fieldName;

    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    return (
        <FormFieldBase
            label={props.fieldDefinition.label}
            error={fieldState.error}
        >
            <input
                type="text"
                disabled
                className={`rounded px-2 py-1.5 text-sm w-full ${fieldState.error ? "bg-red-50 border-red-500 text-red-500" : "border-neutral-200"}`}
                placeholder={props.fieldDefinition.placeholder}
                value={field.value == null ? "" : String(field.value)}
                ref={field.ref}
            />
        </FormFieldBase>
    );
};

export const EventField = ({
    fieldDef,
    emissionRecord,
}: {
    fieldDef: FieldDefinition;
    emissionRecord: EmissionRecordView;
}) => {
    // Determine if field should be visible first
    const [searchParams] = useSearchParams();
    const currentlyOpenSite = searchParams.get("site") ?? "";
    // Most fields have no visibility or suggestion rule — skip the whole-form
    // subscription for them so form changes only re-render fields that evaluate rules.
    const formValues = useWatch({
        disabled:
            !fieldDef.visibleWhen?.logic && !fieldDef.suggestionRule?.logic,
    });
    const context = useMemo(
        () => ({
            emissionRecord,
            values: formValues,
            extra: { currentlyOpenSite },
        }),
        [emissionRecord, formValues, currentlyOpenSite],
    );

    // Compute visibility
    const visible = useMemo(
        () => evaluateVisibility(fieldDef.visibleWhen, context),
        [context, fieldDef.visibleWhen],
    );

    // Suggestion engine
    const form = useFormContext();
    const suggestion = useMemo(() => {
        return evaluateSuggestion(fieldDef.suggestionRule, context);
    }, [fieldDef.suggestionRule, context]);
    const suggestedValue = suggestion?.value;

    // Auto fields always mirror the suggestion, including clearing the value
    // when the rule abstains. Only write on an actual change: setValue notifies
    // useWatch, which recomputes the suggestion and would otherwise loop.
    useEffect(() => {
        if (!visible || fieldDef.fieldType !== "auto") return;
        const name = fieldDef.fieldName;
        if (!name) return;
        const next = suggestedValue ?? null;
        if ((form.getValues(name) ?? null) !== next) {
            form.setValue(name, next);
        }
    }, [suggestedValue, fieldDef.fieldName, fieldDef.fieldType, visible, form]);

    // Autofill fires at most once per field: the first time an autofill-enabled
    // suggestion is available and the field is still empty, we populate it. After
    // that (or if the field already had a value on load) the value is left alone
    // and the suggestion is only offered manually via the SuggestionBox.
    const hasAutofilled = useRef(false);
    useEffect(() => {
        if (hasAutofilled.current) return;
        if (!suggestion?.autofill) return;
        const name = fieldDef.fieldName;
        if (!name) return;
        if (!visible) return;
        hasAutofilled.current = true;
        const current = form.getValues(name);
        if (current == null || current === "") {
            form.setValue(name, suggestion.value);
        }
    }, [suggestion, fieldDef.fieldName, form, visible]);

    // Hide field if not visible
    if (!visible) return null;

    if (fieldDef.fieldType === "text") {
        return (
            <FormTextInput
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }
    if (fieldDef.fieldType === "textarea") {
        return (
            <FormTextArea
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }
    if (fieldDef.fieldType === "number") {
        return (
            <FormNumber
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }
    if (fieldDef.fieldType === "select") {
        return (
            <FormSelect
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }
    if (fieldDef.fieldType === "checkbox") {
        return (
            <FormCheckbox
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }
    if (fieldDef.fieldType === "radio") {
        return (
            <FormYesNoRadio
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }
    if (fieldDef.fieldType === "site") {
        return (
            <FormSite
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }
    if (fieldDef.fieldType === "equipment") {
        return (
            <FormEquipment
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }
    if (fieldDef.fieldType === "datetime") {
        return (
            <FormDateTimeInput
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }
    if (fieldDef.fieldType === "date") {
        return (
            <FormDateInput
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }
    if (fieldDef.fieldType === "user") {
        return (
            <FormUser
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }
    if (fieldDef.fieldType === "starRating") {
        return (
            <FormStarRating
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }
    if (fieldDef.fieldType === "fileUpload") {
        return (
            <FormFieldBase label={fieldDef.label}>
                <EventFiles event={emissionRecord.mainEvent} readonly={false} />
            </FormFieldBase>
        );
    }
    if (fieldDef.fieldType === "emissionLinking") {
        return (
            <FormEventLinking
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
            />
        );
    }
    if (fieldDef.fieldType === "table") {
        return (
            <TableField
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
            />
        );
    }
    if (fieldDef.fieldType === "warning") {
        return (
            <FormWarning
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
            />
        );
    }
    if (fieldDef.fieldType === "auto") {
        return (
            <FormAuto
                fieldDefinition={fieldDef}
                emissionRecord={emissionRecord}
                suggestedValue={suggestedValue}
            />
        );
    }

    return <p className="text-sm mb-0.5 text-neutral-500">{fieldDef.label}</p>;
};
